import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API, useAuth } from "../context/AuthContext";

/**
 * <CustomerCompleteProfile /> — DOB age gate for Google-signed-up customers.
 *
 * Rendered at /account/complete-profile. Blocking modal experience: the user
 * cannot escape without providing DOB (the app also refuses booking-deposit
 * checkout for users with dob_pending=True, so this is a hard requirement).
 */
export default function CustomerCompleteProfile() {
  const { user, authHeader, refreshUser, loading } = useAuth();
  const nav = useNavigate();
  const [dob, setDob] = useState("");
  const [guardianName, setGuardianName] = useState("");
  const [guardianEmail, setGuardianEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login", { replace: true }); return; }
    if (user.role !== "customer") { nav("/", { replace: true }); return; }
    if (user.date_of_birth) { nav("/account", { replace: true }); return; }
  }, [loading, user?.id, user?.date_of_birth, user?.role, nav]);

  if (loading || !user) return null;

  const minor = dob ? computeAge(dob) < 18 : false;

  const submit = async (e) => {
    e.preventDefault();
    if (!dob) { toast.error("Please enter your date of birth"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/google/complete/customer`, {
        date_of_birth: dob,
        guardian_full_name: minor ? guardianName : undefined,
        guardian_email: minor ? guardianEmail : undefined,
      }, { headers: authHeader() });
      await refreshUser();
      toast.success("Welcome to BookFreely");
      nav("/account", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your details");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App" data-testid="customer-complete-profile-page">
      <Header hideSearch />
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Finish setup</div>
        <h1 className="font-display text-3xl mt-2" style={{ color: "var(--gf-text)" }}>One quick thing</h1>
        <p className="text-sm mt-2" style={{ color: "var(--gf-text-muted)" }}>
          BookFreely is age-gated. Enter your date of birth so we can finish creating your account.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <label className="block text-sm" style={{ color: "var(--gf-text-soft)" }}>
            <span>Date of birth</span>
            <input
              data-testid="customer-dob"
              type="date"
              required
              autoComplete="bday"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              className="mt-1 w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            />
          </label>

          {minor && (
            <div data-testid="minor-guardian-block" className="rounded-2xl border p-4" style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-surface-2)" }}>
              <p className="text-xs" style={{ color: "var(--gf-text-muted)" }}>Under 18 — a parent or guardian needs to co-sign your account.</p>
              <label className="mt-3 block text-sm" style={{ color: "var(--gf-text-soft)" }}>
                <span>Guardian full name</span>
                <input data-testid="guardian-name" value={guardianName} onChange={(e) => setGuardianName(e.target.value)}
                       autoComplete="off"
                       className="mt-1 w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]"
                       style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }} required />
              </label>
              <label className="mt-3 block text-sm" style={{ color: "var(--gf-text-soft)" }}>
                <span>Guardian email</span>
                <input data-testid="guardian-email" type="email" value={guardianEmail} onChange={(e) => setGuardianEmail(e.target.value)}
                       autoComplete="off"
                       className="mt-1 w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]"
                       style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }} required />
              </label>
            </div>
          )}

          <button
            type="submit"
            data-testid="customer-complete-submit"
            disabled={submitting}
            className="w-full rounded-full py-2.5 font-medium disabled:opacity-50"
            style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
          >{submitting ? "Saving…" : "Continue"}</button>
        </form>
      </div>
      <Footer />
    </div>
  );
}

function computeAge(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return age;
  } catch { return 0; }
}
